import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isPushSupported, fetchStatus as fetchPushStatus, subscribe as subscribePush, permission as pushPermission } from '@/lib/push';

const DISMISS_KEY = 'qm_reminder_dismissed';

function parseHHMM(s) {
    if (!s) return null;
    const [h, m] = s.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return { h, m };
}

function withinWindow(target, now, windowMinutes = 60) {
    const t = target.h * 60 + target.m;
    const n = now.getHours() * 60 + now.getMinutes();
    const delta = n - t;
    return delta >= 0 && delta <= windowMinutes;
}

/**
 * Soft in-app reminder. Appears when the current time is within 60 minutes
 * after the user's chosen reminder_time, and they haven't dismissed it today.
 * Browser web-push (background notifications) lives in a later batch — this
 * is the always-on, dependency-free part.
 */
export default function ReminderBanner() {
    const { user } = useAuth();
    const [now, setNow] = useState(() => new Date());
    const [hidden, setHidden] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(true);
    const [enabling, setEnabling] = useState(false);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (!user || !isPushSupported()) return;
        fetchPushStatus().then((s) => setPushEnabled(!!s.enabled)).catch(() => {});
    }, [user]);

    const target = useMemo(() => parseHHMM(user?.onboarding?.reminder_time), [user]);

    const shouldShow = useMemo(() => {
        if (!user || !target) return false;
        if (hidden) return false;
        const today = new Date().toISOString().slice(0, 10);
        if (localStorage.getItem(DISMISS_KEY) === today) return false;
        return withinWindow(target, now);
    }, [user, target, hidden, now]);

    if (!shouldShow) return null;

    const dismiss = () => {
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem(DISMISS_KEY, today);
        setHidden(true);
    };

    const enablePush = async () => {
        setEnabling(true);
        try {
            await subscribePush();
            setPushEnabled(true);
        } catch {} finally { setEnabling(false); }
    };

    const showEnableCta =
        isPushSupported() && !pushEnabled && pushPermission() !== 'denied';

    return (
        <div
            className="fixed top-3 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-1.5rem)] qm-glass rounded-full shadow-lg border-white/60 px-3 py-2 flex items-center gap-2 animate-fade-in"
            data-testid="reminder-banner"
            role="status"
        >
            <div className="w-9 h-9 rounded-full bg-sage/40 grid place-items-center shrink-0">
                <Bell size={16} className="text-forest" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-forest leading-snug">
                    Your spot is held. Want a few minutes now?
                </div>
            </div>
            {showEnableCta && (
                <button
                    type="button"
                    onClick={enablePush}
                    disabled={enabling}
                    data-testid="banner-enable-push"
                    className="qm-pill bg-sage/40 border-transparent text-xs px-3 py-1.5 text-forest disabled:opacity-60"
                >
                    {enabling ? '…' : 'Enable'}
                </button>
            )}
            <Link
                to="/app/home"
                data-testid="reminder-go"
                className="qm-pill bg-forest text-cream border-transparent text-xs px-4 py-1.5"
            >
                Begin
            </Link>
            <button
                onClick={dismiss}
                aria-label="Dismiss reminder"
                data-testid="reminder-dismiss"
                className="rounded-full w-8 h-8 grid place-items-center text-forest hover:bg-dusty/40"
            >
                <X size={14} />
            </button>
        </div>
    );
}
